import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, MapPin, TrendingUp, Users } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { InternalLinks } from "@/components/InternalLinks";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";
import { getLocationRelatedLinks } from "@/utils/internalLinksConfig";

const SEOHoustonPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/seo-houston');

  const neighborhoods = [
    "Downtown Houston", "Midtown", "Montrose", "Heights", "Memorial", 
    "Galleria", "Medical Center", "Energy Corridor", "Clear Lake", "Pearland"
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "SEO en Houston",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Houston",
      "containedIn": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": "Servicios profesionales de SEO local para empresas en Houston, TX. Optimización para Google Maps, búsquedas locales y generación de leads."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Por qué necesito SEO local en Houston?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Houston es la 4ta ciudad más grande de EE.UU. con intensa competencia. El SEO local te ayuda a aparecer en Google Maps y búsquedas de 'cerca de mí', capturando clientes en tu zona específica."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cuánto tiempo toma posicionarse en Houston?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Para Google Maps: 4-8 semanas. Para posiciones orgánicas top 3: 3-6 meses dependiendo de competencia en tu sector."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://luisroc.com/" },
      { "@type": "ListItem", "position": 2, "name": "Servicios", "item": "https://luisroc.com/servicios/" },
      { "@type": "ListItem", "position": 3, "name": "SEO Houston", "item": "https://luisroc.com/seo-houston" }
    ]
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>SEO en Houston TX | Consultor de SEO local y técnico – Luis ROC</title>
        <meta 
          name="description" 
          content="Consultoría SEO en Houston: SEO local, Google Business Profile y SEO técnico para empresas que compiten en el área metropolitana. Diagnóstico estratégico inicial." 
        />
        <meta name="keywords" content="seo houston, seo houston tx, agencia seo houston, posicionamiento google houston, seo local houston, consultor seo houston" />
        <link rel="canonical" href={canonicalUrl} />
        
        <meta property="og:title" content="SEO en Houston TX – Luis ROC" />
        <meta property="og:description" content="Posicionamiento local en Houston orientado a convertir demanda en oportunidades medibles. Reunión inicial gratuita." />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Houston" />
      </Helmet>

      <JsonLd data={serviceSchema} />
      <JsonLd data={faqSchema} />
      <JsonLd data={breadcrumbSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav 
            items={[
              { name: "Servicios", url: "/servicios" },
              { name: "Localidades", url: "/servicios-locales-houston" },
              { name: "SEO Houston", url: "/seo-houston" }
            ]}
            language="es"
          />

          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              SEO en Houston para captar la demanda local que ya existe
            </h1>
            
            <div className="max-w-4xl mx-auto mb-8">
              <p className="text-lg text-muted-foreground leading-relaxed">
                Houston es un mercado grande, bilingüe y competitivo: la misma consulta puede
                resolverse en inglés o en español, y con una intención distinta según la zona.
                Desde Downtown hasta Energy Corridor, desde Galleria hasta Medical Center,
                trabajo la parte técnica, el contenido local y el perfil de Google Business
                para que tu negocio aparezca cuando alguien con intención de compra busca en
                tu área. Sin promesas de posición: prioridades claras y medición.
              </p>
            </div>


            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                size="lg" 
                onClick={() => window.location.href = '/diagnostico-seo-gratis'}
              >
                Agenda tu reunión inicial gratuita de 30 minutos
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => window.location.href = '/contacto'}
              >
                Hablar con un Experto
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Houston y área metropolitana
              </span>
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Consultoría directa en inglés y español
              </span>
            </div>

          </section>

          <section className="py-16 bg-accent/5 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">
              ¿Por qué SEO local es crítico en Houston?
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Mercado bilingüe competitivo</h3>
                    <p className="text-muted-foreground">
                      Buena parte de la demanda de Houston se busca en español y en inglés,
                      con vocabulario distinto. Se trabajan ambas audiencias con contenido
                      propio en cada idioma, no con traducción literal.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">La búsqueda local decide la visita</h3>
                    <p className="text-muted-foreground">
                      En consultas con intención local, el map pack y la ficha de Google
                      concentran gran parte de los clics. Si el perfil, las reseñas y las
                      páginas de servicio no están alineados, la demanda se va a otro proveedor.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Complemento —no sustituto— del PPC</h3>
                    <p className="text-muted-foreground">
                      El SEO acumula valor con el tiempo y el PPC compra visibilidad inmediata.
                      Lo útil es decidir qué consulta se cubre con cada canal y medir el coste
                      por lead de ambos, no elegir por ideología.
                    </p>
                  </div>
                </div>

              </Card>
            </div>
          </section>

          <section className="py-16 mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">
              Zonas de Houston que servimos
            </h2>
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
              {neighborhoods.map((neighborhood) => (
                <span 
                  key={neighborhood}
                  className="px-4 py-2 bg-accent rounded-full text-sm font-medium"
                >
                  {neighborhood}
                </span>
              ))}
            </div>
          </section>

          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-6">
              Lidera Houston en Google — comienza hoy
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
              reunión inicial gratuita de 30 minutos para revisar el contexto y definir siguientes pasos
            </p>
            <Button 
              size="lg"
              variant="secondary"
              onClick={() => window.location.href = '/diagnostico-seo-gratis'}
            >
              Agendar reunión inicial gratuita de 30 minutos
            </Button>
          </section>

          <section className="py-16">
            <h2 className="text-2xl font-bold mb-6">Preguntas Frecuentes - SEO Houston</h2>
            <div className="space-y-4 max-w-3xl">
              <Card className="p-6">
                <h3 className="font-semibold mb-2">¿Por qué necesito SEO local en Houston?</h3>
                <p className="text-muted-foreground">
                  Houston es la 4ta ciudad más grande de EE.UU. con intensa competencia. El SEO local te ayuda a aparecer 
                  en Google Maps y búsquedas de "cerca de mí", capturando clientes en tu zona específica.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">¿Cuánto tiempo toma posicionarse en Houston?</h3>
                <p className="text-muted-foreground">
                  Para Google Maps: 4-8 semanas. Para posiciones orgánicas top 3: 3-6 meses dependiendo de competencia en tu sector.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">¿Trabajan con empresas bilingües?</h3>
                <p className="text-muted-foreground">
                  Sí, somos expertos en mercado hispano de Houston. Optimizamos contenido EN/ES y estrategia cultural para maximizar conversión.
                </p>
              </Card>
            </div>
          </section>

          <InternalLinks 
            links={getLocationRelatedLinks('houston', 'es')}
            title="Servicios SEO en Otras Ubicaciones de Texas"
            language="es"
          />
        </main>

        <KnowledgeConnections path="/seo-houston" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default SEOHoustonPage;
